<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import {
  evaluateConditionalRules,
  type ConditionSource,
} from "#bb/lib/conditionalRules";
import { ConditionalRuleEvaluationKey } from "#bb/lib/conditionalRuleState";
import {
  applyLayoutStateOverrides,
  getMergedClasses,
} from "../../composables/useLayoutModel";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});
const emit = defineEmits(["delete", "move", "content-update", "answer-error"]);

/**
 * 0) features => are we in editor mode or just viewing?
 */
const features = inject("render-features", {
  editor: false,
  selectedBlockId: null as string | null,
  resourceKey: "default",
});
const isEditor = Boolean(features.editor);
const canEdit = computed(() => isEditor && (features as any).editable !== false);

/**
 * 1) Access the store & get block data by ID
 */
const {
  getContentById,
  moveItem,
  updateContentById,
  contentState,
  findParentOf,
  getResolvedDataPath,
} = useContentManager();

/**
 * 2) blockData => the actual content object from the store (if found).
 *    In the template, we do e.g. blockData.componentId, blockData.layout
 */
const blockData = computed(() => getContentById(props.id));
const { getCachedAnswer } = useAnswers();
const conditionResourceKey = features.resourceKey || "default";
const conditionValues = useConditionValues(conditionResourceKey);

const conditionalState = computed(() =>
  evaluateConditionalRules(
    blockData.value?.conditions,
    (source: ConditionSource) =>
      source.type === "answer"
        ? getCachedAnswer(
            source.blockId,
            getResolvedDataPath(source.blockId).join("/"),
          )?.value
        : conditionValues.resolveValue(source),
    { visible: true, enabled: true },
    (source: ConditionSource) =>
      source.type === "answer"
        ? getCachedAnswer(
            source.blockId,
            getResolvedDataPath(source.blockId).join("/"),
          ) !== null
        : conditionValues.isAvailable(source),
  )
);
provide(ConditionalRuleEvaluationKey, conditionalState);

/**
 * 3) Context menu logic
 */
// context menu properties are loosely typed; define a local interface to avoid 'never'
interface ContextMenuProperties {
  icon?: string;
  name?: string;
  [key: string]: any;
}
let properties = ref<ContextMenuProperties | null>(null);
let commands = ref<any[]>([]);
let initializeContextMenu: (component: any) => void = () => {};

if (isEditor) {
  const _cm = useContextMenu(props, emit) as any;
  properties = _cm.properties as Ref<ContextMenuProperties | null>;
  commands = _cm.commands as Ref<any[]>;
  initializeContextMenu = _cm.initializeContextMenu as (component: any) => void;
}

/**
 * 4) Resolve the component type using the componentId
 */
const { resolveComponentType } = useRenderer();

/**
 * 5) Drag manager (startDrag, handleMove)
 */
let startDrag: (event: DragEvent, item: any) => void = () => {};
let endDrag: () => void = () => {};
let handleMove: (payload: any) => void = () => {};

if (isEditor) {
  ({ startDrag, endDrag, handleMove } = useDragManager(moveItem));
}

/**
 * 5b) Drag-active flag — used to show chrome labels on all block frames during drag
 */
const isDragActive = isEditor ? useBlockDnd().isDragActive : ref(false);


/**
 * 7) We'll store a ref to the component instance so we can call 'initializeContextMenu'
 */
const component = ref(null);
const filter = ref([]);

if (isEditor) {
  watchEffect(() => {
    if (component.value) {
      initializeContextMenu(component.value);
      filter.value = (component.value as any)?.filter || [];
    }
  });
}

/**
 * 8) ADDING / REMOVING CHILD BLOCKS
 *    Must reference blockData.value.children now,
 *    not props.content.
 */
function addChild(payload: { template: any; index: number }) {
  if (!canEdit.value || !blockData.value) return;
  const newTemplate = JSON.parse(JSON.stringify(payload.template));
  newTemplate.id = crypto.randomUUID();
  if (!newTemplate.children) {
    newTemplate.children = [];
  }

  newTemplate.dataPath = ["{{ parent }}"];
  // Insert child or push
  const childrenArr =
    blockData.value.children || (blockData.value.children = []);
  if (
    typeof payload.index === "number" &&
    payload.index >= 0 &&
    payload.index <= childrenArr.length
  ) {
    childrenArr.splice(payload.index, 0, newTemplate);
  } else {
    childrenArr.push(newTemplate);
  }
}

/**
 * 9) Deleting a child
 *    Also needs to reference blockData.value.children
 */
function deleteChild(child: any) {
  if (!canEdit.value || !blockData.value?.children) return;
  const index = blockData.value.children.indexOf(child);
  if (index !== -1) {
    blockData.value.children.splice(index, 1);
  }
}

function expandCollapseContent(block: any) {
  if (!canEdit.value) return;
  updateContentById(block.id, { collapsed: !block.collapsed });
}

const mergedClasses = computed(() =>
  blockData.value
    ? getMergedClasses(applyLayoutStateOverrides(blockData.value.layout, {
        visible: conditionalState.value.hasVisibilityRules
          && conditionalState.value.visibilityReady
          ? conditionalState.value.visible
          : undefined,
        enabled: conditionalState.value.hasEnabledRules
          && conditionalState.value.enabledReady
          ? conditionalState.value.enabled
          : undefined,
      }))
    : ""
);
const shouldRender = computed(
  () =>
    !conditionalState.value.hasVisibilityRules
    || !conditionalState.value.visibilityReady
    || conditionalState.value.visible
);
const conditionalDisabled = computed(() =>
  conditionalState.value.hasEnabledRules
  && conditionalState.value.enabledReady
  && !conditionalState.value.enabled
    ? true
    : undefined
);

useComponentConditionState(
  () => props.id,
  () => ({
    visible: blockData.value?.hidden !== true && shouldRender.value,
    enabled: conditionalDisabled.value !== true,
  }),
  { clearBlockOnUnmount: true },
);

const usesGridLayoutWrapper = computed(
  () => blockData.value?.componentId === ComponentDefinitionIds.NAVIGATION_PAGE
);

const isSelected = computed(() => (features as any)?.selectedBlockId === props.id);
const collaboratorNames = computed<string[]>(() =>
  (features as any)?.collaboratorsByBlock?.[props.id] ?? [],
);

const { getBlock } = useBlockRegistry();

const blockIcon = computed(() => {
  if (properties.value?.icon) return properties.value.icon;
  return getBlock(blockData.value?.componentId)?.icon || blockData.value?.icon || 'lucide:box';
});

const blockLabel = computed(() => {
  return properties.value?.name
    || blockData.value?.name
    || blockData.value?.title
    || getBlock(blockData.value?.componentId)?.type
    || blockData.value?.componentId
    || blockData.value?.id;
});

const resolvedDataPath = computed(() =>
  blockData.value ? getResolvedDataPath(blockData.value.id).join("/") : "",
);

const hasChildren = computed(() => Array.isArray(blockData.value?.children) && blockData.value.children.length > 0);
const childCount = computed(() => blockData.value?.children?.length ?? 0);

// True when this block is the immediate parent of the currently selected block,
// so it can show a subtle "parent-active" highlight (matches the prototype).
const isParentActive = computed(() => {
  if (!isEditor) return false;
  const selectedId = (features as any)?.selectedBlockId;
  if (!selectedId || selectedId === props.id) return false;
  const parent = findParentOf(selectedId, contentState.value ?? []);
  return parent?.id === props.id;
});

function selectForEditing() {
  if (!features.editor || !blockData.value) return;
  (features as any).selectedBlockId = props.id;
  eventBus.emit("resource-editor:select-block", {
    id: props.id,
    block: blockData.value,
    properties: properties.value,
    commands: commands.value,
  });
}

function showInHierarchy() {
  if (!blockData.value) return;
  selectForEditing();
  eventBus.emit("resource-editor:reveal-in-hierarchy", { id: blockData.value.id });
}
</script>

<template>
  <!-- EDITOR MODE: block frame with chrome bar -->
  <div
    v-if="features.editor"
    :class="['block-frame', $attrs.class]"
    :data-selected="isSelected"
    :data-parent-active="isParentActive"
    :data-drag-active="isDragActive || undefined"
    :data-collaboration-focus="collaboratorNames.length > 0 || undefined"
    :data-collapsed="!!blockData?.collapsed"
    :data-testid="'block-' + blockData?.id"
    :data-block-name="properties?.name || blockData?.componentId"
    :data-block-id="blockData?.id"
    :draggable="canEdit"
    @click.stop="selectForEditing"
    @dragstart="(event) => canEdit && startDrag(event, blockData)"
    @dragend="canEdit && endDrag()"
  >
    <!-- Chrome: edge label tab + control cluster -->
    <div class="block-chrome">
      <button
        type="button"
        class="block-chrome__label"
        @click.stop="selectForEditing"
      >
        <Icon mode="svg" size="14" :name="blockIcon" />
        <span>{{ blockLabel }}</span>
      </button>
      <!-- Actions pill — only visible when the block is selected -->
      <div v-if="isSelected" class="block-chrome__actions">
        <Popover>
          <PopoverTrigger as-child>
            <Button
              variant="ghost"
              size="icon"
              class="size-5 shrink-0"
              title="Block details"
              :draggable="false"
              @click.stop
              @pointerdown.stop
              @dragstart.stop.prevent
            >
              <Icon mode="svg" size="12" name="lucide:id-card" />
              <span class="sr-only">Show block details</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" class="w-auto p-3" @click.stop>
            <RendererIdentityCard
              :id="blockData?.id || id"
              :label="blockLabel || blockData?.id || id"
              :icon="blockIcon"
              :path="resolvedDataPath"
              show-hierarchy-action
              @show-hierarchy="showInHierarchy"
            />
          </PopoverContent>
        </Popover>
      </div>
      <span
        v-if="collaboratorNames.length"
        class="block-chrome__collaborators"
        :title="`${collaboratorNames.join(', ')} editing this block`"
      >
        <Icon mode="svg" size="12" name="lucide:users" />
        <span>{{ collaboratorNames[0] }}</span>
        <span v-if="collaboratorNames.length > 1">+{{ collaboratorNames.length - 1 }}</span>
      </span>
      <div class="block-chrome__controls">
        <!-- <Button
          variant="ghost" size="icon"
          class="size-6 cursor-grab active:cursor-grabbing"
          draggable="true"
          title="Drag to move"
          @click.stop
          @dragstart.stop="(event: DragEvent) => startDrag(event, blockData)"
          @dragend="endDrag"
        >
          <Icon mode="svg" size="14" name="lucide:grip-vertical" />
        </Button> -->
        <Button
          variant="ghost" size="icon"
          class="size-5"
          title="Collapse / Expand"
          :disabled="!canEdit"
          @click.stop="expandCollapseContent(blockData)"
        >
          <Icon mode="svg" size="12" :name="blockData?.collapsed ? 'material-symbols:expand-all-rounded' : 'material-symbols:collapse-all-rounded'" />
        </Button>
        <!-- <Button
          variant="ghost" size="icon"
          class="size-6"
          title="More"
          @click.stop="selectForEditing"
        >
          <Icon mode="svg" size="14" name="lucide:ellipsis" />
        </Button> -->
      </div>
    </div>

    <!-- Collapsed summary -->
    <div v-if="blockData?.collapsed" class="block-chrome__collapsed">
      <Icon mode="svg" size="14" :name="blockIcon" />
      <span>{{ blockLabel }}</span>
      <span v-if="hasChildren" class="block-chrome__pill">{{ childCount }} items</span>
    </div>

    <!-- Body -->
    <Suspense v-else>
      <div class="contents">
        <div v-if="blockData && usesGridLayoutWrapper" :class="cn(mergedClasses)">
          <component
            :is="resolveComponentType(blockData?.componentId)"
            :id="blockData?.id"
            ref="component"
            @content-update="(value) => emit('content-update', value)"
            @answer-error="(payload) => emit('answer-error', payload)"
          >
            <template v-if="blockData?.children">
              <div v-for="(child, idx) in blockData.children" :key="child.id">
                <renderer-drop-zone
                  :targetId="blockData.id"
                  :index="Number(idx)"
                  :filter-type="filter || ''"
                  :editable="canEdit"
                  @move="handleMove"
                  @add-block="addChild"
                />
                <renderer :id="child.id" @delete="deleteChild" @move="handleMove" />
              </div>

              <renderer-drop-zone
                :targetId="blockData.id"
                :index="blockData.children.length"
                :filter-type="filter || ''"
                :editable="canEdit"
                @move="handleMove"
                @add-block="addChild"
              />
            </template>
          </component>
        </div>

        <component
          v-else-if="blockData"
          :is="resolveComponentType(blockData?.componentId)"
          :id="blockData?.id"
          ref="component"
          :class="cn(mergedClasses)"
          @content-update="(value) => emit('content-update', value)"
          @answer-error="(payload) => emit('answer-error', payload)"
        >
          <template v-if="blockData?.children">
            <div v-for="(child, idx) in blockData.children" :key="child.id">
              <renderer-drop-zone
                :targetId="blockData.id"
                :index="Number(idx)"
                :filter-type="filter || ''"
                :editable="canEdit"
                @move="handleMove"
                @add-block="addChild"
              />
              <renderer :id="child.id" @delete="deleteChild" @move="handleMove" />
            </div>

            <renderer-drop-zone
              :targetId="blockData.id"
              :index="blockData.children.length"
              :filter-type="filter || ''"
              :editable="canEdit"
              @move="handleMove"
              @add-block="addChild"
            />
          </template>
        </component>
      </div>
    </Suspense>
  </div>

  <!-- VIEWER MODE -->
  <Suspense v-else-if="blockData && blockData.hidden !== true && shouldRender">
    <div class="contents" :data-block-id="blockData.id">
      <div v-if="usesGridLayoutWrapper" :class="cn(mergedClasses)">
        <component
          :is="resolveComponentType(blockData.componentId)"
          :id="blockData.id"
          :disabled="conditionalDisabled"
          :aria-disabled="conditionalDisabled"
          @content-update="(value) => emit('content-update', value)"
          @answer-error="(payload) => emit('answer-error', payload)"
        >
          <template v-if="blockData.children">
            <renderer
              v-for="(child, index) in blockData.children"
              :key="child.id"
              :id="child.id"
            />
          </template>
        </component>
      </div>

      <component
        v-else
        :is="resolveComponentType(blockData.componentId)"
        :id="blockData.id"
        :class="cn(mergedClasses)"
        :disabled="conditionalDisabled"
        :aria-disabled="conditionalDisabled"
        @content-update="(value) => emit('content-update', value)"
        @answer-error="(payload) => emit('answer-error', payload)"
      >
        <!-- Render nested children in a viewer-only scenario -->
        <template v-if="blockData.children">
          <renderer
            v-for="(child, index) in blockData.children"
            :key="child.id"
            :id="child.id"
          />
        </template>
      </component>
    </div>

    <template #fallback>
      <loading-indicator />
    </template>
  </Suspense>
</template>
